import React, { useState, useEffect } from 'react';
import {
  Modal,
  Steps,
  Button,
  Space,
  Typography,
  Input,
  App,
  Alert,
  Spin,
  Divider,
  List,
  Tag
} from 'antd';
import {
  QrcodeOutlined,
  SafetyOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  KeyOutlined
} from '@ant-design/icons';
import { QRCodeCanvas } from 'qrcode.react';
import authService from '../services/authService';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

const TwoFactorSetup = ({ visible, onComplete, onCancel }) => {
  const { message } = App.useApp();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [manualEntryKey, setManualEntryKey] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);

  useEffect(() => {
    if (visible && currentStep === 0) {
      setupTwoFactor();
    }
  }, [visible]);

  const setupTwoFactor = async () => {
    setLoading(true);
    try {
      const result = await authService.setup2FA();
      if (result.success) {
        setQrCodeData(result.data.qrCode);
        setManualEntryKey(result.data.secret);
        setCurrentStep(1);
      } else {
        message.error(result.message || 'Failed to setup 2FA');
        onCancel();
      }
    } catch (error) {
      message.error('Failed to setup 2FA');
      onCancel();
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      message.error('Please enter a valid 6-digit code');
      return;
    }

    setVerifying(true);
    try {
      const result = await authService.verify2FASetup(verificationCode);
      if (result.success) {
        setBackupCodes(result.data.backupCodes);
        setCurrentStep(2);
        message.success('2FA verified successfully!');
      } else {
        message.error(result.message || 'Invalid verification code');
      }
    } catch (error) {
      message.error('Failed to verify code');
    } finally {
      setVerifying(false);
    }
  };

  const handleComplete = () => {
    resetState();
    onComplete();
  };

  const handleCancel = () => {
    resetState();
    onCancel();
  };

  const resetState = () => {
    setCurrentStep(0);
    setQrCodeData(null);
    setManualEntryKey('');
    setVerificationCode('');
    setBackupCodes([]);
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(`${label} copied to clipboard`);
    }).catch(() => {
      message.error('Failed to copy');
    });
  };

  const steps = [
    {
      title: 'Setup',
      icon: <QrcodeOutlined />
    },
    {
      title: 'Verify',
      icon: <SafetyOutlined />
    },
    {
      title: 'Backup Codes',
      icon: <KeyOutlined />
    }
  ];

  return (
    <Modal
      title="Enable Two-Factor Authentication"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      maskClosable={false}
    >
      <Steps current={currentStep} style={{ marginBottom: '32px' }}>
        {steps.map((step) => (
          <Step key={step.title} title={step.title} icon={step.icon} />
        ))}
      </Steps>

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <Paragraph style={{ marginTop: '16px' }}>Setting up 2FA...</Paragraph>
        </div>
      )}

      {!loading && currentStep === 1 && (
        <div>
          <Alert
            message="Scan QR Code"
            description="Open your authenticator app (Google Authenticator, Authy, etc.) and scan this QR code"
            type="info"
            showIcon
            style={{ marginBottom: '24px' }}
          />

          <div style={{ 
            textAlign: 'center', 
            padding: '24px',
            background: '#fafafa',
            borderRadius: '8px',
            marginBottom: '24px'
          }}>
            {qrCodeData && (
              <QRCodeCanvas 
                value={qrCodeData} 
                size={200}
                level="H"
                includeMargin
              />
            )}
          </div>

          <Divider>OR</Divider>

          <div style={{ marginBottom: '24px' }}>
            <Text strong style={{ fontSize: '15px', color: '#2c3e50' }}>Manual Entry Key:</Text>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginTop: '12px'
            }}>
              <Input
                value={manualEntryKey}
                readOnly
                size="large"
                style={{ 
                  fontFamily: 'monospace',
                  borderRadius: '6px',
                  border: '1px solid #ddd'
                }}
              />
              <Button
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(manualEntryKey, 'Secret key')}
              >
                Copy
              </Button>
            </div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Enter this key manually if you can't scan the QR code
            </Text>
          </div>

          <Divider />

          <div>
            <Text strong style={{ fontSize: '15px', color: '#2c3e50' }}>Verification Code:</Text>
            <div style={{ marginTop: '12px', marginBottom: '24px' }}>
              <Input
                placeholder="6-digit verification code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                size="large"
                maxLength={6}
                style={{ 
                  fontSize: '18px', 
                  letterSpacing: '6px',
                  textAlign: 'center',
                  fontFamily: 'monospace',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  padding: '12px'
                }}
              />
            </div>

            <div style={{ textAlign: 'right' }}>
              <Space>
                <Button onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={handleVerify}
                  loading={verifying}
                  disabled={verificationCode.length !== 6}
                  icon={<CheckCircleOutlined />}
                >
                  Verify & Enable 2FA
                </Button>
              </Space>
            </div>
          </div>
        </div>
      )}

      {!loading && currentStep === 2 && (
        <div>
          <Alert
            message="2FA Enabled Successfully!"
            description="Save these backup codes in a secure location. You can use them to access your account if you lose your authenticator device."
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            style={{ marginBottom: '24px' }}
          />

          <div style={{
            background: '#fafafa',
            padding: '24px',
            borderRadius: '8px',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <Text strong style={{ fontSize: '16px' }}>
                <KeyOutlined /> Backup Recovery Codes
              </Text>
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(backupCodes.join('\n'), 'Backup codes')}
              >
                Copy All
              </Button>
            </div>

            <List
              grid={{ gutter: 16, xs: 1, sm: 2, md: 2 }}
              dataSource={backupCodes}
              renderItem={(code, index) => (
                <List.Item>
                  <div style={{
                    padding: '12px',
                    background: 'white',
                    borderRadius: '4px',
                    border: '1px solid #d9d9d9',
                    textAlign: 'center',
                    fontFamily: 'monospace',
                    fontSize: '14px'
                  }}>
                    <Tag color="blue" style={{ marginRight: '8px' }}>
                      {index + 1}
                    </Tag>
                    {code}
                  </div>
                </List.Item>
              )}
            />
          </div>

          <Alert
            message="Important"
            description={
              <ul style={{ marginBottom: 0, paddingLeft: '20px' }}>
                <li>Each backup code can only be used once</li>
                <li>Store these codes in a secure password manager</li>
                <li>You can generate new backup codes from your profile at any time</li>
              </ul>
            }
            type="warning"
            showIcon
            style={{ marginBottom: '24px' }}
          />

          <div style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              size="large"
              onClick={handleComplete}
              icon={<CheckCircleOutlined />}
            >
              I've Saved My Backup Codes
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default TwoFactorSetup;
